import { useState } from "react";
import { useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import SelectedWhiteCheckbox from "@/assets/ux/SelectedWhiteCheckbox.svg?react";
import UnselectedWhiteCheckbox from "@/assets/ux/UnselectedWhiteCheckbox.svg?react";
import { Button } from "@/components/Interface/Button/Button";
import { Checkbox } from "@/components/Interface/FormFields/Checkbox/Checkbox";
import {
  PasswordStrength,
  ValidationToken,
} from "@/components/Interface/PasswordStrength/PasswordStrength";
import { TextField } from "@/components/Interface/TextField/TextFields";
import { LoadingSpinner } from "@/components/Layout/LoadingSpinner/LoadingSpinner";
import { VerifyEmailModal } from "@/components/Welcome/VerifyEmailModal";
import { useAlert } from "@/hooks/useAlert";
import { useCustomerLogin } from "@/hooks/useCustomerLogin";
import { useCreateCustomer } from "@/services/api";
import { CreateCustomerRequest } from "@/services/model";
import { FCC } from "@/types";
import { AlertTypes } from "@/utils/atoms";
import { getLanguageId } from "@/utils/customerUtils";
import { createRegisterErrorHandler } from "@/utils/registerErrorHandler";

interface Props {
  afterRegister?: () => void;
  handleVerifyEmailModalClose?: () => void;
}

type RegisterFormFields = {
  confirmPassword: string;
  emailAddress: string;
  password: string;
  termsAccepted: boolean;
};

export const RegisterForm: FCC<Props> = ({
  children,
  handleVerifyEmailModalClose,
  afterRegister,
}) => {
  const {
    handleSubmit,
    register,
    setError,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<RegisterFormFields>({
    defaultValues: {
      confirmPassword: "",
      emailAddress: "",
      password: "",
      termsAccepted: false,
    },
    mode: "onChange",
  });

  const { i18n, t } = useTranslation();
  const { shouldShowVerifyEmailModal, setShouldShowVerifyEmailModal } =
    useCustomerLogin();
  const showAlert = useAlert();
  const navigate = useNavigate();
  const [showPasswordStrength, setShowPasswordStrength] =
    useState<boolean>(false);

  const [isAfterRegisterLoginLoading, setIsAfterRegisterLoginLoading] =
    useState(false);
  const registerErrorHandler = createRegisterErrorHandler(
    setError,
    t,
    showAlert,
  );

  const baseLanguage = i18n.language.split("-")[0];

  const { mutate: createCustomer, isLoading } = useCreateCustomer({
    request: {
      headers: {
        "X-Skip-Auth": "true",
      },
    },
    mutation: {
      onSuccess: () => {
        showAlert({
          text: t(
            "translation:newWelcome.registerForm.uiFeedback.registrationSuccess",
          ),
          type: AlertTypes.success,
        });

        // Start login process after successful registration
        setIsAfterRegisterLoginLoading(true);
        // Show verifyEmailModal to proceed with Login (user will be authorized after finishing email verification)
        setShouldShowVerifyEmailModal(true);
      },
      onError: (error) => {
        registerErrorHandler(error);
      },
    },
  });

  const onSubmit = (data: RegisterFormFields) => {
    if (data.termsAccepted !== true) {
      setError("termsAccepted", {
        type: "manual",
        message: t(
          "translation:newWelcome.registerForm.validation.acceptTerms",
        ),
      });

      return;
    }

    const createCustomerRequest: CreateCustomerRequest = {
      header: {
        client: 1,
        source: 1,
        lastUser: 1,
      },
      basicCustomerData: {
        language: { id: getLanguageId(t, baseLanguage) },
        email: data.emailAddress,
        mainAddress: {
          country: { id: 200 },
          zip: "0000",
          city: "Dummy",
          street: "Dummy",
        },
      },
      authentification: {
        login: data.emailAddress,
        password: data.password,
      },
    };

    createCustomer({ data: createCustomerRequest });
  };

  const passwordRequirements: ValidationToken[] = [
    {
      regex: /^.{8,30}$/,
      displayMessage: t(
        "translation:newWelcome.registerForm.passwordRequirements.minLength",
      ),
    },
    {
      regex: /[=+\-^$*.[\]{}()/?"!@#%&/<>:;|_~]/,
      displayMessage: t(
        "translation:newWelcome.registerForm.passwordRequirements.specialCharacter",
      ),
    },
    {
      regex: /(?=.*[a-z])(?=.*[A-Z])/,
      displayMessage: t(
        "translation:newWelcome.registerForm.passwordRequirements.upperLowercase",
      ),
    },
    {
      regex: /\d/,
      displayMessage: t(
        "translation:newWelcome.registerForm.passwordRequirements.numberRequired",
      ),
    },
  ];

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="max-w-[420px] md:m-auto md:h-max md:w-full"
    >
      <h1 className="mb-10 text-4xl font-semibold text-white">
        {t("newWelcome.registerForm.title")}
      </h1>

      <TextField
        id="emailAddress"
        error={errors.emailAddress}
        inputType="email"
        labelText={t("translation:Common.generalForm.fields.email.label")}
        placeholder={t(
          "translation:Common.generalForm.fields.email.placeholder",
        )}
        register={register("emailAddress", {
          required: {
            value: true,
            message: t(
              "translation:Common.generalForm.validation.emailRequired",
            ),
          },
          pattern: {
            value: /\S+@\S+\.\S+/,
            message: t(
              "translation:Common.generalForm.validation.emailNotValid",
            ),
          },
        })}
      />

      <TextField
        id="password"
        customStyles={{ container: "mb-0" }}
        error={errors.password}
        inputType="password"
        labelText={t("translation:Common.generalForm.fields.password.label")}
        placeholder={t(
          "translation:Common.generalForm.fields.password.placeholder",
        )}
        register={register("password", {
          required: {
            value: true,
            message: t(
              "translation:Common.generalForm.validation.passwordRequired",
            ),
          },
          validate: (value: string) => {
            return passwordRequirements.every((req) => req.regex.test(value));
          },
        })}
        onFocus={() => {
          setShowPasswordStrength(true);
        }}
        onBlur={() => {
          setShowPasswordStrength(false);
        }}
      />
      <div className="mb-4">
        {showPasswordStrength && (
          <PasswordStrength
            password={watch("password")}
            validationTokens={passwordRequirements}
          />
        )}
      </div>

      <TextField
        id="confirm-password"
        error={errors.confirmPassword}
        inputType="password"
        labelText={t(
          "translation:newWelcome.registerForm.confirmPassword.label",
        )}
        placeholder={t(
          "translation:newWelcome.registerForm.confirmPassword.placeholder",
        )}
        register={register("confirmPassword", {
          required: {
            value: true,
            message: t(
              "translation:newWelcome.registerForm.validation.confirmPasswordRequired",
            ),
          },
          validate: (value: string) =>
            value === watch("password") ||
            t("translation:newWelcome.registerForm.validation.passwordNoMatch"),
        })}
        //! Workaround since onBlur doesn't work on passwordConfirm
        onFocus={() => {
          setShowPasswordStrength(false);
        }}
      />

      <div className="mt-10">
        <Checkbox
          selectedIcon={<SelectedWhiteCheckbox />}
          unselectedIcon={<UnselectedWhiteCheckbox />}
          id="termsAccepted"
          name="termsAccepted"
          labelComponent={
            <p className="max-w-full text-xs text-white">
              <Trans
                i18nKey="translation:newWelcome.registerForm.acceptTerms"
                components={{
                  1: (
                    <a
                      href={t("translation:Common.links.privacyPolicy")}
                      className="font-semibold underline"
                      target="_blank"
                      rel="noreferrer"
                    />
                  ),
                  2: (
                    <a
                      href={t("translation:Common.links.terms&Conditions")}
                      className="font-semibold underline"
                      target="_blank"
                      rel="noreferrer"
                    />
                  ),
                }}
              />
            </p>
          }
          register={register}
          options={{
            required: {
              value: true,
              message: t(
                "translation:newWelcome.registerForm.validation.acceptTerms",
              ),
            },
          }}
          errors={errors}
        />

        {errors.termsAccepted && (
          <p className="mt-1 text-sm text-red-500">
            {errors.termsAccepted.message}
          </p>
        )}

        {shouldShowVerifyEmailModal && (
          <VerifyEmailModal
            email={watch("emailAddress")}
            password={watch("password")}
            handleClose={() => {
              setShouldShowVerifyEmailModal(false);
              setIsAfterRegisterLoginLoading(false);

              if (!!handleVerifyEmailModalClose) {
                handleVerifyEmailModalClose();
              }
            }}
            handleSuccess={() => {
              if (!!afterRegister) {
                afterRegister();
              }

              setShouldShowVerifyEmailModal(false);
              navigate("/onboarding/1/", { replace: true });
            }}
          />
        )}
      </div>

      <Button
        fullWidth
        className="accent mt-4"
        disabled={isLoading || isSubmitting || isAfterRegisterLoginLoading}
        type="submit"
      >
        {isAfterRegisterLoginLoading ? (
          <LoadingSpinner />
        ) : (
          t("translation:newWelcome.registerForm.title")
        )}
      </Button>

      {children}
    </form>
  );
};
